import { createGroundElevation } from '../../site/groundElevation';
import type { InstancedMesh } from 'three';
import type { PolygonWithHoles, Vec2 } from '../../geo/types';
import type { Rng } from '../../core/rng';
import type { SiteModel } from '../../site/types';
import type { BuildContext, Obstacle } from '../types';
import { fromRing } from '../../geo/clip';
import { centroid, pointInPolygon } from '../../geo/polygon';
import { crownKeepOut, roadKeepOut } from '../country/keepOut';
import {
  drawTreeShape,
  placeYardTrees,
  type IsBlocked,
  type TreePlacement,
} from '../country/trees';
import { buildTrees } from '../country/vegetation';
import { collectReservedLandmarks } from './landmarks/landmarkAccess';
import { WOODCUT_GROUND, type WoodcutGround } from './woodcut/woodcutGround';
import { WOODCUT_TREES } from './woodcut/woodcutTrees';
import { oudekerkYardTrees } from './surveyed/oudekerkYardPlan';

export interface CityTreeMeshes {
  readonly meshes: readonly InstancedMesh[];
  readonly yardTrees: number;
}

/*
 * Where no city tree may stand: within a crown's reach of a house, wall, tower, gate or complete
 * landmark precinct and private approach, within a trunk's girth of a street, or on water.
 */
export function cityTreeBlocker(
  obstacles: readonly Obstacle[],
  site: Pick<SiteModel, 'streets' | 'queries'>,
  landmarks: BuildContext['layout']['landmarks'] = []
): IsBlocked {
  // Walking collision opens court gates and separates the actual architectural parts. Those
  // openings are access, not ordinary back yards in which to generate new trees.
  const buildings = crownKeepOut([
    ...obstacles.map((o) => fromRing(o.polygon)),
    ...collectReservedLandmarks(landmarks).map((reserved) => reserved.polygon),
  ]);
  const roads = roadKeepOut(site.streets);
  return (p) => site.queries.isWater(p) || buildings.contains(p) || roads.contains(p);
}

/* A block has no id, so its stream is named after where it lies; dropping one leaves the rest. */
function blockStream(block: PolygonWithHoles): string {
  const [x, y] = centroid(block.outer);
  return `yard:${x.toFixed(1)},${y.toFixed(1)}`;
}

/*
 * PLAN §5: the city's trees are yard trees in its blocks. Gardens, orchards and cloisters lie
 * outside the blocks, and the countryside plants them.
 */
export function placeCityTrees(
  blocks: readonly PolygonWithHoles[],
  isBlocked: IsBlocked,
  rng: Rng
): readonly TreePlacement[] {
  return blocks.flatMap((polygon) =>
    placeYardTrees({ polygon }, isBlocked, rng.fork(blockStream(polygon)))
  );
}

/*
 * The trees the 1544 woodcut draws in the blocks, each where nothing built since stands in its way,
 * and the yard trees of the blocks only on the ground the woodcut was not read on: where it was
 * read, it shows which yards had trees. A tree it draws in a garden, orchard or cloister is left to
 * the countryside, which plants those.
 */
export function placeDrawnTrees(
  drawn: readonly Vec2[],
  yard: readonly TreePlacement[],
  at: { readonly blocks: readonly PolygonWithHoles[]; readonly isBlocked: IsBlocked },
  rng: Rng,
  ground: WoodcutGround = WOODCUT_GROUND
): readonly TreePlacement[] {
  return [
    ...yard.filter((tree) => ground.at(tree.position) === 'unread'),
    ...drawn
      .filter((p) => at.blocks.some((block) => pointInPolygon(p, block)) && !at.isBlocked(p))
      .map((p) => drawTreeShape(p, rng.fork(`drawn:${p[0]},${p[1]}`))),
  ];
}

/* Every city tree in one trunk mesh and at most three crown meshes, numbered after the houses. */
export function buildCityTrees(
  ctx: BuildContext,
  obstacles: readonly Obstacle[],
  rng: Rng,
  firstEdgeIndex: number
): CityTreeMeshes {
  const isBlocked = cityTreeBlocker(obstacles, ctx.site, ctx.layout.landmarks);
  /* The woodcut turns the Oude Kerk against its streets, so the trees of its yard are read by hand. */
  const drawn = [...WOODCUT_TREES, ...oudekerkYardTrees(ctx.layout).flatMap((row) => row.trees)];
  const trees = placeDrawnTrees(
    drawn,
    placeCityTrees(ctx.site.buildable, isBlocked, rng.fork('trees')),
    { blocks: ctx.site.buildable, isBlocked },
    rng.fork('drawnTrees')
  );
  const meshes = buildTrees(
    trees,
    ctx.palette,
    rng.fork('treeMeshes'),
    ctx.materials.painted({ instanced: true }),
    firstEdgeIndex,
    createGroundElevation(ctx.layout)
  );
  return { meshes: meshes.meshes, yardTrees: trees.length };
}
